import { Table, Input, Button, Select, message, Modal } from "antd";
import { PlusOutlined, LeftOutlined, BulbOutlined } from "@ant-design/icons";
import { Edit, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { getRequest, deleteRequest } from "../../../../../service/index";
import Location from '../../../../../modals/location';
import EditLocation from '../../../../../modals/editLocation';
import ModalWrapper from "../../../../../Modal";
import { getEmployeeData } from "../../../../../utils/localStorage";

const WorkLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [location, setLocationOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const employeeData = getEmployeeData();
  const userRole = employeeData?.role;
  const workLocationPermissions = employeeData?.designation?.permissions?.Organization?.['Work Location'] || [];

  const canView = userRole === "companyadmin" || workLocationPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || workLocationPermissions.includes("add");
  const canEdit = userRole === "companyadmin" || workLocationPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || workLocationPermissions.includes("delete");

  const handleCloseLocation = () => {
    setLocationOpen(false);
    fetchLocations();
  };

  const handleCloseEditLocation = () => {
    setEditModalOpen(false);
    setSelectedLocation(null);
    fetchLocations();
  };

  const handleEdit = (record) => {
    if (!canEdit) {
      message.warning("You don't have permission to edit locations.");
      return;
    }
    setSelectedLocation(record);
    setEditModalOpen(true);
  };

  // Fetch locations data
  const fetchLocations = async () => {
    setLoading(true);
    try {
      if (!canView) {
        setLocations([]);
      } else {
        const response = await getRequest("/api/companies/locations");
        if (response.locations) {
          setLocations(response.locations);
        }
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      message.error("Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  // Delete location
  const handleDelete = (record) => {
    if (!canDelete) {
      message.warning("You don't have permission to delete locations.");
      return;
    }
    Modal.confirm({
      title: 'Are you sure you want to delete this location?',
      content: `This will permanently delete the location "${record.locationName}" and all its associated data.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        try {
          const response = await deleteRequest(`/api/companies/locations?locationId=${record._id}`);
          if (response.message === "Location deleted successfully") {
            message.success('Location deleted successfully');
            fetchLocations();
          } else {
            message.error(response.message || 'Failed to delete location');
          }
        } catch (error) {
          console.error('Error deleting location:', error);
          message.error('Failed to delete location');
        }
      },
    });
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Filter locations based on search text
  const filteredLocations = locations.filter(location =>
    location.locationName?.toLowerCase().includes(searchText.toLowerCase()) ||
    location.city?.toLowerCase().includes(searchText.toLowerCase()) ||
    location.address?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Location Name",
      dataIndex: "locationName",
      key: "locationName",
      render: (text) => <span className="font-medium">{text}</span>,
    },
    {
      title: "Employees",
      dataIndex: "employeeCount",
      key: "employeeCount",
      render: (count) => <span className="font-medium">{count || 0}</span>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
    },
    
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<Edit className="w-4 h-4 text-blue-600" />}
            className="text-blue-600 hover:text-blue-800"
            disabled={!canEdit}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            icon={<Trash2 className="w-4 h-4 text-red-600" />}
            className="text-red-600 hover:text-red-800"
            disabled={!canDelete}
            onClick={() => handleDelete(record)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
    <div className="px-4 pt-2 pb-4 flex flex-col gap-5 bg-white rounded-md">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold mb-4">Work Locations</h2>
        <div className="flex justify-between items-center gap-2">
          <Button icon={<LeftOutlined />}>Back</Button>
          <Button icon={<BulbOutlined />}>Page Tip</Button>
        </div>
      </div>
      <div className="flex justify-between items-center gap-4">
        <Input.Search 
          placeholder="Search locations..." 
          style={{ width: 300 }} 
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="bg-primary text-white"
          disabled={!canAdd}
          onClick={() => canAdd ? setLocationOpen(true) : message.warning("You don't have permission to add locations.")}
        >
          Add Location
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={canView ? filteredLocations : []}
        rowKey="_id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          total: canView ? filteredLocations.length : 0,
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} locations`,
        }}
        className="bg-white rounded-lg border border-gray-200"
      />
      <div className="flex justify-between items-center mt-4"></div>
    </div>
    <ModalWrapper
        open={location}
        onRequestClose={() => setLocationOpen(false)}
        title="add location"
      >
        <Location onClose={handleCloseLocation} />
      </ModalWrapper>
      <ModalWrapper
        open={editModalOpen}
        onRequestClose={handleCloseEditLocation}
        title="edit location"
      >
        <EditLocation onClose={handleCloseEditLocation} location={selectedLocation} />
      </ModalWrapper>
      {!canView && (
        <div className="text-center text-gray-500 py-4">You don't have permission to view locations.</div>
      )}
    </>
  );
};

export default WorkLocations;
